import {Card, CardContent, CardActions, Button, Typography, Box, Chip} from '@mui/material';
import {Add, School} from '@mui/icons-material';
import {ExamModule} from '@cp/model';
import {env} from '../../config/env';

interface CoursesAvailableGroupCardProps {
  module: ExamModule;
  onSelect: (module: ExamModule) => void;
}

export default function CoursesAvailableGroupCard({module, onSelect}: CoursesAvailableGroupCardProps) {
  const subjectConfig = module.subject ? env.subjects[module.subject] : env.subjects.MATHEMATICS;
  const levelConfig = module.level ? env.levels[module.level] : env.levels.GCSE;

  return (
    <Card
      data-testid="available-module-card"
      data-module-id={module.id}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: `2px solid ${subjectConfig.border}`,
        borderRadius: 3,
        overflow: 'hidden',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: 6,
        },
      }}
    >
      {/* Subject Header */}
      <Box
        sx={{
          bgcolor: subjectConfig.bg,
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography variant="h4" sx={{fontSize: '2rem'}}>
          {subjectConfig.icon}
        </Typography>
        <Box sx={{flex: 1}}>
          <Typography variant="caption" sx={{fontWeight: 600, color: subjectConfig.border, textTransform: 'uppercase'}}>
            {subjectConfig.label}
          </Typography>
        </Box>
      </Box>

      <CardContent sx={{flexGrow: 1, p: 2.5}}>
        <Typography variant="h6" sx={{fontWeight: 600, mb: 1.5, lineHeight: 1.3}} data-testid="available-module-name">
          {module.name}
        </Typography>
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2}}>
          {levelConfig && (
            <Chip
              label={levelConfig.label}
              size="small"
              sx={{
                bgcolor: subjectConfig.bg,
                color: subjectConfig.border,
                border: `1px solid ${subjectConfig.border}`,
                fontWeight: 500,
              }}
            />
          )}
          {module.examBoard && (
            <Chip
              label={module.examBoard}
              size="small"
              sx={{
                bgcolor: 'grey.100',
                color: 'text.primary',
                border: '1px solid',
                borderColor: 'grey.300',
                fontWeight: 500,
              }}
            />
          )}
          {module.requiredRole && (
            <Chip
              label={`${module.requiredRole}`}
              size="small"
              sx={{
                bgcolor: 'grey.900',
                color: 'grey.100',
                border: '1px solid',
                borderColor: 'grey.700',
                fontWeight: 500,
                textTransform: 'uppercase',
              }}
            />
          )}
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mt: 2}}>
          <School sx={{fontSize: 18, color: 'text.secondary'}} />
          <Typography variant="body2" color="text.secondary" sx={{fontWeight: 500}}>
            {module.topics?.length || 0} topics available
          </Typography>
        </Box>
      </CardContent>
      <CardActions sx={{p: 2, pt: 0}}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => onSelect(module)}
          fullWidth
          data-testid="select-course-button"
          data-module-id={module.id}
          sx={{
            bgcolor: subjectConfig.border,
            '&:hover': {
              bgcolor: subjectConfig.border,
              opacity: 0.9,
            },
            fontWeight: 600,
            py: 1.5,
          }}
        >
          Select Course
        </Button>
      </CardActions>
    </Card>
  );
}
